import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link as LinkIcon } from "lucide-react";
import { api, resolveImg, formatApiError } from "@/lib/adminApi";

export default function ImageField({ value, onChange, testid = "image" }) {
  const [mode, setMode] = useState("upload"); // 'upload' | 'url'
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <label className="eyebrow">Image</label>
        <div className="flex gap-1 ml-auto">
          <button
            type="button"
            onClick={() => setMode("upload")}
            data-testid={`${testid}-mode-upload`}
            className={`px-3 py-1 text-xs font-accent uppercase tracking-widest border ${mode === "upload" ? "text-bone" : "text-bone-dim"}`}
            style={{ borderColor: "var(--hairline)", background: mode === "upload" ? "var(--oxblood)" : "transparent" }}
          >
            <Upload size={12} className="inline mr-1" /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode("url")}
            data-testid={`${testid}-mode-url`}
            className={`px-3 py-1 text-xs font-accent uppercase tracking-widest border ${mode === "url" ? "text-bone" : "text-bone-dim"}`}
            style={{ borderColor: "var(--hairline)", background: mode === "url" ? "var(--oxblood)" : "transparent" }}
          >
            <LinkIcon size={12} className="inline mr-1" /> URL
          </button>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div
          className="w-28 h-28 flex-none border overflow-hidden flex items-center justify-center"
          style={{ borderColor: "var(--hairline)", background: "#0B0B08" }}
        >
          {value ? (
            <img src={resolveImg(value)} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <span className="eyebrow" style={{ color: "var(--bone-dim)" }}>None</span>
          )}
        </div>

        <div className="flex-1">
          {mode === "upload" ? (
            <>
              <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testid}-file`} />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="btn-outline disabled:opacity-60"
                data-testid={`${testid}-upload-btn`}
              >
                {uploading ? "Uploading…" : "Choose File"}
              </button>
              <p className="font-body text-sm text-bone-dim mt-2">JPG / PNG / WEBP · up to 8MB</p>
            </>
          ) : (
            <input
              type="url"
              value={value?.startsWith("http") ? value : ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://images.unsplash.com/…"
              data-testid={`${testid}-url-input`}
              className="w-full bg-transparent border-b outline-none py-2 font-body text-base text-bone"
              style={{ borderColor: "var(--brass)", cursor: "text" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
